import { useMostBoughtCommon } from "@/elements-react/most-bought/common";
import classNames from "classnames";
import style from "@/elements-react/most-bought/style.module.scss";
import { createProductUrl } from "@/util/category";
import { useServices } from "@/services-context";
import { LayoutStyleTypes, LayoutTypes, useLayoutType } from "@/util-react/layout";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { ProductElementMostBought } from "@/elements-react/product-element-refactor/ProductElementMostBought";
import {B2cProductPayload} from "@/generated/graphql/b2c";
import arrowUpIcon from "@/assets/arrow-up.svg";
import arrowDownIcon from "@/assets/arrow-down.svg";
import { Icon } from "@/elements-react/fix-icon";
import { Gtag } from "@/services/gtag";
import {FEATURED_PRODUCTS_LISTENER_NAME} from "@/services/featured-products.ts";
import {Categories} from "@/services/categories.ts";

const XL_CARD_WIDTH = 262;
const XL_CARD_MAX_WIDTH = XL_CARD_WIDTH * 3;
const MD_CARD_WIDTH = 244;
const MD_CARD_MAX_WIDTH = MD_CARD_WIDTH * 3;
const LG_GAP = 16;
const SM_CARD_WIDTH = 172;
const SM_CARD_MAX_WIDTH = SM_CARD_WIDTH * 3;
const SM_GAP = 8;

function calcSize(card: number, gap: number, count: number) {
  return card * count + gap * (count - 1);
}

// ToDo need make refactoring, implement similar logic in a special method, without constant rebranding when changing the width of the window
function getSizeClassNames(type: string, width: number) {
  let cardWidth, cardMaxWidth;
  switch (type) {
    case LayoutStyleTypes.TYPE_XXL:
    case LayoutStyleTypes.TYPE_XL:
      cardWidth = XL_CARD_WIDTH;
      cardMaxWidth = XL_CARD_MAX_WIDTH;
      break;
    case LayoutStyleTypes.TYPE_LG:
    case LayoutStyleTypes.TYPE_MD:
      cardWidth = MD_CARD_WIDTH;
      cardMaxWidth = MD_CARD_MAX_WIDTH;
      break;
    case LayoutStyleTypes.TYPE_SM:
    case LayoutStyleTypes.TYPE_XS:
    case LayoutStyleTypes.TYPE_XXS:
    default:
      cardWidth = SM_CARD_WIDTH;
      cardMaxWidth = SM_CARD_MAX_WIDTH;
      cardWidth = SM_CARD_WIDTH;
  }
  const gap = SM_GAP;

  return {
    "col-8": width > calcSize(cardWidth, gap, 8),
    "col-5": width > calcSize(cardWidth, gap, 5) && width <= calcSize(cardWidth, gap, 8),
    "col-4": width > calcSize(cardWidth, gap, 4) && width <= calcSize(cardWidth, gap, 5),
    "col-3": width > calcSize(cardWidth, gap, 3) && width <= calcSize(cardWidth, gap, 4),
    "col-2": width > calcSize(cardWidth, gap, 2) && width <= calcSize(cardWidth, gap, 3),
    "col-1": width <= calcSize(cardWidth, gap, 2),
    limit: width > calcSize(cardMaxWidth, gap, 8),
  };
}

const layoutFactory = () => {
  const { layoutType, layoutStyleType } = useLayoutType();
  const isMobile = layoutType === LayoutTypes.TYPE_MOBILE;
  const [classMap, setClassMap] = useState<{ [key: string]: boolean }>({ "col-1": isMobile, "col-2": false, "col-4": !isMobile, "col-8": false, limit: false });
  const ref = useRef<HTMLDivElement>(null);
  const type = layoutStyleType;

  const measure = useCallback(() => {
    if (typeof window === "undefined" || !ref.current) return;
    const width = ref.current.getBoundingClientRect().width;
    setClassMap(getSizeClassNames(type, width));
  }, [type]);

  const gridStyle = useMemo(() => {
    if (type === LayoutStyleTypes.TYPE_LG) {
      return { gap: `${LG_GAP}px`, minWidth: `${XL_CARD_WIDTH}px` };
    } else {
      return { gap: `${SM_GAP}px`, minWidth: `${SM_CARD_WIDTH}px` };
    }
  }, [type]);

  useEffect(() => {
    measure();
    if (typeof window === "undefined") return;
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  return {
    ref,
    classMap,
    type,
    gridStyle,
  };
};

const getPrice = (exchange: B2cProductPayload) => {
  const price = exchange.prices?.[0]?.price.amount * 100 ?? 0;
  return [price, price];
};

const productFromData = (item: any, i: number, categories: Categories) => {
  const price = item.prices ? getPrice(item) : [item.minPrice, item.maxPrice];

  return {
    id: item.id,
    name: item.productInfo?.name ?? "",
    mpn: item.mpn,
    brand: item.brandName,
    price,
    images: item.productAssets.filter((asset: any) => asset.type === "image"),
    url: createProductUrl(item, categories),
    originalMpn: item.originalMpn,
    exchanges:
        item.exchanges?.map((exchange: any) => productFromData({ ...exchange, originalMpn: item.mpnFormatted, brandName: exchange.brand?.name })) ?? [],
    crosses: item.analogues,
    state: item.state,
    stockType: item.stockType,
    isHidePriceIfProductIsOutOfStock: item.isHidePriceIfProductIsOutOfStock,
    deliveryStandardTimesIsNotDefined: item.deliveryStandardTimesIsNotDefined,
    googleEventData: {
      item_name: `${item.mpn} ${item.brandName} ${item.name}`,
      item_id: item.id,
      price: price[0],
      item_brand: item.brandName,
      item_list_name: "Most bought section of home page",
      index: i,
      quantity: "1",
    },
  };
};

const productsFactory = (products: any, categories: Categories) => {
  return products?.map((item: any, i: number) => productFromData(item, i, categories)) ?? []
};

const googleEventViewItemList = (products) => {
  const itemsGoogleEvent = products.map((item) => {
    return item.googleEventData;
  });

  Gtag.pushEventEcommerce("view_item_list", { items: itemsGoogleEvent });
};

export const MostBoughtDesktopBlackAndBlue = () => {
  const { ref, classMap, type, gridStyle } = layoutFactory();
  const { featuredProducts, categories } = useServices([FEATURED_PRODUCTS_LISTENER_NAME]);
  const products = featuredProducts.getProducts();
  const items = useMemo(() => productsFactory(products, categories), [products]);

  if (!items.length) {
    return null;
  }

  useEffect(() => {
    googleEventViewItemList(items);
  }, []);

  return (
    <div className={classNames(style.container)}>
      <div className={classNames(style.titleContainer)}>
        <h2 className={classNames(style.title)}>Most bought today</h2>
        <p className={classNames(style.description)}>Top-selling items ready for pickup</p>
      </div>
      <div
        ref={ref}
        className={classNames(
          style.productContainer,
          classMap["col-8"] && style["col-8"],
          classMap["col-5"] && style["col-5"],
          classMap["col-4"] && style["col-4"],
          classMap["col-3"] && style["col-3"],
          classMap["col-2"] && style["col-2"],
          classMap["col-1"] && style["col-1"],
          classMap.limit && style.limit,
        )}
        style={classMap.limit && type === LayoutStyleTypes.TYPE_LG ? { ...gridStyle, maxWidth: `${XL_CARD_MAX_WIDTH}px` } : gridStyle}
      >
        {items.map((item) => {
          return <ProductElementMostBought key={item.id} {...item} type={LayoutStyleTypes.TYPE_LG} />;
        })}
      </div>
    </div>
  );
};

export const MostBoughtMobileBlackAndBlue = () => {
  const { ref, classMap, type, gridStyle } = layoutFactory();
  const [isShortList, setIsShortList] = useState(true);
  const { featuredProducts, categories } = useServices([FEATURED_PRODUCTS_LISTENER_NAME]);
  const products = featuredProducts.getProducts();
  const items = useMemo(() => productsFactory(products, categories), [products]);

  if (!items.length) {
    return null;
  }

  const renderItems = isShortList ? items.slice(0, 4) : items;

  return (
    <div className={classNames(style.container)}>
      <div className={classNames(style.titleContainer)}>
        <h2 className={classNames(style.title)}>Most bought today</h2>
        <p className={classNames(style.description)}>Top-selling items ready for pickup</p>
      </div>
      <div style={{ width: "stretch" }}>
        <div
          ref={ref}
          className={classNames(
            style.productContainer,
            classMap["col-8"] && style["col-8"],
            classMap["col-5"] && style["col-5"],
            classMap["col-4"] && style["col-4"],
            classMap["col-3"] && style["col-2"],
            classMap["col-2"] && style["col-2"],
            classMap["col-1"] && style["col-1"],
            classMap.limit && style.limit,
          )}
          style={classMap.limit && type === LayoutStyleTypes.TYPE_LG ? { ...gridStyle, maxWidth: `${XL_CARD_MAX_WIDTH}px` } : gridStyle}
        >
          {renderItems.map((item) => {
            return <ProductElementMostBought key={item.id} {...item} type={LayoutStyleTypes.TYPE_LG} />;
          })}
        </div>
      </div>
      {renderItems.length >= 4 && (
        <div className={classNames(style.showMoreButton)} onClick={() => setIsShortList(!isShortList)}>
          {isShortList ? (
            <>
              Show more <Icon src={arrowDownIcon} width={16} height={16} />
            </>
          ) : (
            <>
              Show less <Icon src={arrowUpIcon} width={16} height={16} />
            </>
          )}
        </div>
      )}
    </div>
  );
};
